import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Server, Plus, Edit2, Trash2, Check, RefreshCw, Gamepad2, MessageSquare, ExternalLink, Globe } from "lucide-react";

export default function IDGServersTab() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);

  const [form, setForm] = useState({
    name: "",
    address: "",
    game_type: "cs16",
    dns: "",
    forum_board_name: "",
    forum_board_id: "",
    description: "",
    order: 0,
  });

  const fetchServers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/servers/list");
      setServers(res.data || []);
    } catch (err) {
      toast.error("Eroare la încărcarea serverelor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const handleOpenAdd = () => {
    setEditingServer(null);
    setForm({
      name: "",
      address: "",
      game_type: "cs16",
      dns: "",
      forum_board_name: "",
      forum_board_id: "",
      description: "",
      order: servers.length + 1,
    });
    setShowModal(true);
  };

  const handleOpenEdit = (srv) => {
    setEditingServer(srv);
    setForm({
      name: srv.name || "",
      address: srv.address || "",
      game_type: srv.game_type || "cs16",
      dns: srv.dns || "",
      forum_board_name: srv.forum_board_name || "",
      forum_board_id: srv.forum_board_id || "",
      description: srv.description || "",
      order: srv.order || 0,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address) {
      toast.error("Completează numele și adresa serverului!");
      return;
    }

    try {
      if (editingServer) {
        await api.put(`/servers/manage/${editingServer.id}`, form);
        toast.success("Server actualizat cu succes!");
      } else {
        await api.post("/servers/manage", form);
        toast.success("Server adăugat cu succes!");
      }
      setShowModal(false);
      fetchServers();
    } catch (err) {
      toast.error("Eroare la salvarea serverului.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur dorești să ștergi acest server?")) return;
    try {
      await api.delete(`/servers/manage/${id}`);
      toast.success("Server șters!");
      fetchServers();
    } catch (err) {
      toast.error("Eroare la ștergerea serverului.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-[#0d0f17] border border-white/10 p-6 rounded-2xl shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[var(--tf-primary)]/15 border border-[var(--tf-primary)]/30 flex items-center justify-center text-[var(--tf-primary)]">
            <Server className="w-6 h-6" />
          </div>
          <div>
            <h2 className="font-['Rajdhani'] text-2xl font-extrabold text-white uppercase tracking-wide">
              Panou de Control Servere
            </h2>
            <p className="text-zinc-400 text-xs font-mono mt-0.5">
              Adaugă, editează și gestionează serverele de joc (CS 1.6, CS2, CS:GO) și serverele Discord.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchServers}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white transition-all"
            title="Reîmprospătează"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleOpenAdd}
            className="py-2.5 px-4 bg-[var(--tf-primary)] hover:bg-orange-600 text-white font-mono font-bold text-xs uppercase tracking-wider rounded-xl shadow-lg hover:shadow-[0_0_20px_rgba(255,79,0,0.4)] transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Adaugă Server
          </button>
        </div>
      </div>

      {/* Servers List Grid */}
      <div className="grid gap-4">
        {servers.map((srv) => (
          <div 
            key={srv.id} 
            className="bg-[#0e1017] border border-white/10 hover:border-white/20 rounded-2xl p-4 flex items-center justify-between gap-4 transition-all shadow-lg"
          >
            <div className="flex items-center gap-3.5 min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 ${srv.game_type === 'discord' ? 'bg-[#5865F2]/20 text-[#5865F2] border border-[#5865F2]/40' : 'bg-orange-500/20 text-orange-400 border border-orange-500/40'}`}>
                {srv.game_type === 'discord' ? <MessageSquare className="w-5 h-5" /> : <Gamepad2 className="w-5 h-5" />}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase ${srv.game_type === 'discord' ? 'bg-[#5865F2]/20 text-[#5865F2] border border-[#5865F2]/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'}`}>
                    {srv.game_type?.toUpperCase() || 'CS 1.6'}
                  </span>
                  <h3 className="font-['Rajdhani'] font-extrabold text-sm text-white truncate">
                    {srv.name}
                  </h3>
                </div>

                <div className="flex items-center gap-3 text-xs font-mono text-zinc-400 mt-1">
                  <span>Adresă / IP: <strong className="text-white">{srv.address}</strong></span>
                  {srv.dns && <span>Domeniu: <strong className="text-zinc-300">{srv.dns}</strong></span>}
                  {srv.forum_board_name && <span className="text-amber-400">Forum: {srv.forum_board_name}</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleOpenEdit(srv)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white transition-all"
                title="Editează"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(srv.id)}
                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500 border border-red-500/30 text-red-400 hover:text-white transition-all"
                title="Șterge"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0e1017] border border-white/15 rounded-2xl p-6 max-w-xl w-full space-y-5 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <h3 className="font-['Rajdhani'] text-lg font-extrabold uppercase text-white">
                {editingServer ? "Editează Server" : "Adaugă Server Nou"}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-zinc-400 hover:text-white font-mono text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="text-xs font-mono text-zinc-400 block mb-1">Nume Server Complet</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex: CS 1.6 #1 [CLASSIC DUST2]"
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono text-zinc-400 block mb-1">Adresă IP / Cod Invite Discord</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="ex: 209.38.247.243:27016 sau CrGC77X8W"
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs font-mono text-zinc-400 block mb-1">Tip Server / Versiune</label>
                  <select
                    value={form.game_type}
                    onChange={(e) => setForm({ ...form, game_type: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                  >
                    <option value="cs16">CS 1.6 Clasic</option>
                    <option value="mix">Server Turneu / Mix (CS 1.6)</option>
                    <option value="cs2">CS2</option>
                    <option value="csgo">CS:GO</option>
                    <option value="discord">Discord Voice Server</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono text-zinc-400 block mb-1">Domeniu / DNS (Opțional)</label>
                  <input
                    type="text"
                    value={form.dns}
                    onChange={(e) => setForm({ ...form, dns: e.target.value })}
                    placeholder="ex: dd2.1s2k.ro"
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono text-zinc-400 block mb-1">Nume Forum Categorie</label>
                  <input
                    type="text"
                    value={form.forum_board_name}
                    onChange={(e) => setForm({ ...form, forum_board_name: e.target.value })}
                    placeholder="ex: CS 1.6 #1 Section"
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-mono text-zinc-400 block mb-1">Descriere Server</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descriere scurtă a serverului..."
                  rows={2}
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-2.5 text-sm text-white focus:border-[var(--tf-primary)] outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="py-2.5 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 font-mono text-xs"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="py-2.5 px-6 rounded-xl bg-[var(--tf-primary)] hover:bg-orange-600 text-white font-mono font-bold text-xs uppercase tracking-wider shadow-lg"
                >
                  Salvează Server
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
